<template>
    <div class="space-y-4 p-4">
        <!-- Field Requirements Hint -->
        <div v-if="hint" class="px-3 py-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800 flex items-start gap-2">
            <UIcon name="i-ion-information-circle" class="shrink-0 mt-0.5 w-4 h-4" />
            <span>{{ hint }}</span>
        </div>

        <!-- Drag & Drop Zone -->
        <div class="border-2 border-dashed rounded-lg p-8 text-center transition-colors" :class="[
            isDragging ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-slate-400'
        ]" @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop">
            <UIcon name="i-ion-cloud-upload" class="w-12 h-12 mx-auto text-slate-400 mb-3" />
            <p class="text-slate-600 mb-2">
                Drag and drop a CSV or Excel file here, or
            </p>
            <UButton color="neutral" variant="outline" @click="triggerFileInput" :disabled="disabled">
                Browse Files
            </UButton>
            <input ref="fileInput" type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" :disabled="disabled" class="hidden" @change="handleFileSelect" />
            <p class="text-sm text-slate-500 mt-2">
                Supports .csv and .xlsx files
            </p>
        </div>

        <p v-if="filename" class="text-sm text-slate-600">Selected: {{ filename }}</p>
        <p v-if="fileError" role="alert" class="text-sm text-red-600">{{ fileError }}</p>
        <!-- Divider -->
        <div class="flex items-center space-x-3">
            <div class="flex-1 border-t border-slate-200" />
            <span class="text-sm text-slate-500">or paste data</span>
            <div class="flex-1 border-t border-slate-200" />
        </div>

        <!-- Text Area for Pasting -->
        <UFormField label="Paste CSV data">
            <UTextarea v-model="localRawText" :rows="8" :disabled="disabled"
                :placeholder="placeholder" />
        </UFormField>

        <!-- Preview -->
        <div v-if="parsedHeaders.length > 0" class="bg-slate-50 rounded p-3">
            <p class="text-sm font-medium text-slate-700 mb-2">
                Preview ({{ totalRowCount > parsedRows.length ? '~' + totalRowCount : parsedRows.length }} rows detected)
            </p>
            <div class="overflow-x-auto">
                <table class="text-xs w-full">
                    <thead>
                        <tr class="border-b">
                            <th v-for="header in parsedHeaders" :key="header"
                                class="px-2 py-1 text-left text-slate-500 font-medium">
                                {{ header }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(row, i) in parsedRows.slice(0, 3)" :key="i" class="border-b last:border-0">
                            <td v-for="(cell, j) in row" :key="j" class="px-2 py-1 text-slate-700 truncate max-w-32">
                                {{ cell || '-' }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <p v-if="totalRowCount > 3" class="text-xs text-slate-500 mt-2">
                ... and {{ (totalRowCount > parsedRows.length ? '~' + (totalRowCount - 3) : parsedRows.length - 3) }} more rows
            </p>
        </div>
    </div>
</template>

<script setup lang="ts">

const props = defineProps<{
    fields?: FieldDef[];
    hint?: string;
    disabled?: boolean;
}>();

const rawText = defineModel<string>('rawText', { required: true });
const filename = defineModel<string | null>('filename', { required: true });

const emit = defineEmits<{
    file: [File];
    parse: [{ headers: string[]; rows: string[][] }];
}>();

// Generate placeholder based on fields
const placeholder = computed(() => {
    if (!props.fields || props.fields.length === 0) {
        return 'Paste your CSV data here...\n\nExample:\nemail,first_name,last_name\njohn@example.com,John,Doe';
    }

    // Get field labels for header row (up to 3 fields for readability)
    const displayFields = props.fields.slice(0, 3);
    const headerRow = displayFields.map(f => f.label.toLowerCase().replace(/\s+/g, '_')).join(',');

    // Generate example values based on field keys
    const exampleValues = displayFields.map(f => {
        switch (f.key) {
            case 'company_url':
                return 'https://acme.com';
            case 'company_name':
                return 'Acme Inc';
            case 'company_linkedin_url':
                return 'https://linkedin.com/company/acme';
            case 'email':
                return 'john@example.com';
            case 'first_name':
                return 'John';
            case 'last_name':
                return 'Doe';
            case 'full_name':
                return 'John Doe';
            case 'title':
                return 'CEO';
            case 'linkedin_url':
                return 'https://linkedin.com/in/johndoe';
            default:
                return 'value';
        }
    }).join(',');

    return `Paste your CSV data here...\n\nExample:\n${headerRow}\n${exampleValues}`;
});

const fileInput = ref<HTMLInputElement>();
const isDragging = ref(false);
const localRawText = ref('');
const parsedHeaders = ref<string[]>([]);
const parsedRows = ref<string[][]>([]);
const totalRowCount = ref(0);
const fileError = ref('');

function triggerFileInput() {
    fileInput.value?.click();
}

async function handleFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) await processFile(file);
    input.value = '';
}

async function handleDrop(event: DragEvent) {
    isDragging.value = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) await processFile(file);
}

async function processFile(file: File) {
    if (props.disabled) return;
    fileError.value = '';
    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (extension !== '.xlsx' && extension !== '.csv') {
        fileError.value = 'Unsupported file type. Choose a .csv or .xlsx file.';
        return;
    }
    rawText.value = '';
    localRawText.value = '';
    filename.value = file.name;
    emit('file', file);
}

function parseAndEmit(text: string) {
    if (!text.trim()) {
        parsedHeaders.value = [];
        parsedRows.value = [];
        totalRowCount.value = 0;
        emit('parse', { headers: [], rows: [] });
        return;
    }
    const preview = parseCSV(text, 100);
    parsedHeaders.value = preview.headers;
    parsedRows.value = preview.rows;
    // Estimate total rows by counting line breaks (fast even for multi-MB text)
    totalRowCount.value = preview.rows.length < 100
        ? preview.rows.length
        : text.split('\n').length - 1;
    emit('parse', preview);
}

// Sync local -> parent and parse
watch(localRawText, (val) => {
    if (val !== rawText.value) {
        rawText.value = val;
        filename.value = null; // Pasted data has no filename
    }
    parseAndEmit(val);
});

// Sync parent -> local
watch(rawText, (val) => {
    if (val !== localRawText.value) {
        localRawText.value = val;
    }
}, { immediate: true });
</script>
